import React, { useContext, useEffect, useState } from "react";
import { Container, Row, Col, Card, Button, Spinner } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";

import { FaCheck, FaMapMarkerAlt, FaStar } from "react-icons/fa";

import { FiShoppingCart, FiArrowLeft } from "react-icons/fi";

import axios from "axios";
import Swal from "sweetalert2";

import MyNavbar from "../../navbar/Navbar";
import Footer from "../../footer/Footer";
import AppContext from "../../../../context/AppContext";

import "../../../../assets/css/user/dealdetail/dealdetails.css";

import MarqueeBanner from "../../Marqueebanner/Marqueebanner";
import SimpleBanner from "../../general/SimpleBanner";

export default function DealDetails() {
  const { id } = useParams();
  const navigate = useNavigate();

  const { baseUrl, baseUrlForFiles } = useContext(AppContext);

  // =========================================================
  // STATES
  // =========================================================

  const [deal, setDeal] = useState(null);
  const [loading, setLoading] = useState(true);

  const [isProcessing, setIsProcessing] = useState(false);
  const [isInCart, setIsInCart] = useState(false);

  // Customer login state
  const [loggedInCustomer, setLoggedInCustomer] = useState(null);

  // =========================================================
  // CHECK CUSTOMER LOGIN
  // =========================================================

  const checkCustomerLogin = () => {
    try {
      const savedCustomer = localStorage.getItem("loggedInUser");

      if (savedCustomer) {
        const customer = JSON.parse(savedCustomer);

        if (customer && customer.id) {
          setLoggedInCustomer(customer);
        } else {
          setLoggedInCustomer(null);
        }
      } else {
        setLoggedInCustomer(null);
      }
    } catch (error) {
      console.log("Customer login check error:", error);
      setLoggedInCustomer(null);
    }
  };

  // Check login when page loads
  useEffect(() => {
    checkCustomerLogin();
  }, []);

  // =========================================================
  // GET DEAL DETAILS
  // =========================================================

  const getDealDetails = () => {
    setLoading(true);

    const formData = new FormData();

    formData.append("tpdeal_id", id);

    axios
      .post(`${baseUrl}/customer/deals/viewDealDetails`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then((response) => {
        console.log("Deal Details:", response.data);

        if (response.data.success) {
          setDeal(response.data.deal);
        } else {
          setDeal(null);
        }
      })
      .catch((error) => {
        console.log("Deal Details Error:", error);

        setDeal(null);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (id) {
      getDealDetails();
    }
  }, [id]);

  // =========================================================
  // CHECK CART STATUS
  // =========================================================

  const checkCartStatus = () => {
    // Customer login nahi hai
    if (!loggedInCustomer || !id) {
      setIsInCart(false);
      return;
    }

    const formData = new FormData();

    formData.append("tpcustomer_id", loggedInCustomer.id);

    axios
      .post(`${baseUrl}/customer/cart/getCart`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then((response) => {
        console.log("Cart:", response.data);

        const cartDeals = response.data.deals || [];

        const exists = cartDeals.some(
          (item) => Number(item.tpdeal?.id) === Number(id),
        );

        setIsInCart(exists);
      })
      .catch((error) => {
        console.log("Cart status error:", error);

        setIsInCart(false);
      });
  };

  useEffect(() => {
    if (deal && loggedInCustomer) {
      checkCartStatus();
    } else {
      setIsInCart(false);
    }
  }, [deal, loggedInCustomer]);

  // =========================================================
  // ADD TO CART
  // =========================================================

  const addToCart = () => {
    // =====================================================
    // LOGIN CHECK
    // =====================================================

    if (!loggedInCustomer) {
      Swal.fire({
        icon: "info",
        title: "Login Required",
        text: "Please login first to add this deal to your cart.",
        confirmButtonText: "Login",
        showCancelButton: true,
        cancelButtonText: "Cancel",
      }).then((result) => {
        if (result.isConfirmed) {
          navigate("/login");
        }
      });

      return;
    }

    // =====================================================
    // SOLD CHECK
    // =====================================================

    if (deal?.status === "Sold") {
      Swal.fire({
        icon: "warning",
        title: "Sold Out",
        text: "This deal has already been sold.",
        confirmButtonText: "Okay",
      });

      return;
    }

    // =====================================================
    // EXPIRED CHECK
    // =====================================================

    if (deal?.expires_at && new Date(deal.expires_at) <= new Date()) {
      Swal.fire({
        icon: "warning",
        title: "Deal Expired",
        text: "This deal has already expired.",
        confirmButtonText: "Okay",
      });

      return;
    }

    // =====================================================
    // ALREADY IN CART
    // =====================================================

    if (isInCart) {
      Swal.fire({
        icon: "info",
        title: "Already in Cart",
        text: "This deal is already in your cart.",
        confirmButtonText: "Go to Cart",
        showCancelButton: true,
        cancelButtonText: "Stay Here",
      }).then((result) => {
        if (result.isConfirmed) {
          navigate("/cart");
        }
      });

      return;
    }

    // =====================================================
    // ADD TO CART
    // =====================================================

    const formData = new FormData();

    formData.append("tpcustomer_id", loggedInCustomer.id);

    formData.append("tpdeal_id", deal.id);

    setIsProcessing(true);

    axios
      .post(`${baseUrl}/customer/cart/addToCart`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then((response) => {
        console.log("Add Cart:", response.data);

        setIsInCart(true);

        // Navbar cart count update
        window.dispatchEvent(new Event("cartUpdated"));

        Swal.fire({
          icon: "success",
          title: "Added to Cart",
          text:
            deal?.deal_type === "Instant Deals" &&
            deal?.start_at &&
            new Date(deal.start_at) > new Date()
              ? "Deal added to cart. You can purchase it when the deal starts."
              : "Deal added to your cart successfully.",
          timer: 1800,
          showConfirmButton: false,
        });
      })
      .catch((error) => {
        console.log("Add to cart error:", error);

        Swal.fire({
          icon: "error",
          title: "Error",
          text: error?.response?.data?.message || "Unable to add deal to cart.",
        });
      })
      .finally(() => {
        setIsProcessing(false);
      });
  };

  // =========================================================
  // LOADING
  // =========================================================

  if (loading) {
    return (
      <div className="deal-details-page">
        {/* =================================================
            MARQUEE
        ================================================= */}
        <MarqueeBanner />

        {/* =================================================
            NAVBAR
        ================================================= */}
        <MyNavbar />

        {/* =================================================
            SIMPLE BANNER
        ================================================= */}
        <SimpleBanner
          title="All Deals"
          subtitle="Discover amazing deals from verified sellers."
          breadcrumbs={[
            {
              label: "Home",
              href: "/",
            },
            {
              label: "All Deals",
              href: "/deals",
            },
            {
              label: "Deal Details",
              current: true,
            },
          ]}
        />

        {/* =================================================
            LOADING
        ================================================= */}
        <div className="deal-details-loading">
          <Spinner animation="border" />

          <p>Loading deal details...</p>
        </div>

        {/* =================================================
            FOOTER
        ================================================= */}
        <Footer />
      </div>
    );
  }

  // =========================================================
  // DEAL NOT FOUND
  // =========================================================

  if (!deal) {
    return (
      <div className="deal-details-page">
        {/* MARQUEE */}
        <MarqueeBanner />

        {/* NAVBAR */}
        <MyNavbar />

        {/* BANNER */}
        <SimpleBanner
          title="All Deals"
          subtitle="Discover amazing deals from verified sellers."
          breadcrumbs={[
            {
              label: "Home",
              href: "/",
            },
            {
              label: "All Deals",
              href: "/deals",
            },
            {
              label: "Deal Details",
              current: true,
            },
          ]}
        />

        {/* NOT FOUND */}
        <Container className="py-5 text-center">
          <h3>Deal not found</h3>

          <p className="text-muted mt-2">
            The deal you are looking for does not exist or has been removed.
          </p>

          <Button className="mt-3" onClick={() => navigate("/deals")}>
            Back to Deals
          </Button>
        </Container>

        {/* FOOTER */}
        <Footer />
      </div>
    );
  }

  // =========================================================
  // DEAL STATUS
  // =========================================================

  const isNotStarted =
    deal.deal_type === "Instant Deals" &&
    deal.start_at &&
    new Date(deal.start_at) > new Date();

  const isSold = String(deal.status).toLowerCase() === "sold";

  const isExpired = deal.expires_at && new Date(deal.expires_at) <= new Date();

  // =========================================================
  // SERVICES
  // =========================================================

  let services = [];

  try {
    services =
      typeof deal.services === "string"
        ? JSON.parse(deal.services)
        : deal.services || [];
  } catch (error) {
    console.log("Services JSON error:", error);

    services = [];
  }

  // =========================================================
  // RATING
  // =========================================================

  const rating = Number(deal.deal_rating || 0);

  const reviews = Number(deal.deal_reviews || 0);

  // =========================================================
  // FORMAT PAKISTAN DATE TIME
  // =========================================================

  const formatPakistanDateTime = (dateTime) => {
    if (!dateTime) return "—";

    const date = new Date(dateTime);

    if (isNaN(date.getTime())) {
      return "—";
    }

    return date.toLocaleString("en-PK", {
      timeZone: "Asia/Karachi",
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  // =========================================================
  // MAIN RETURN
  // =========================================================

  return (
    <div className="deal-details-page">
      {/* =====================================================
          MARQUEE
      ===================================================== */}

      <MarqueeBanner />

      {/* =====================================================
          NAVBAR
      ===================================================== */}

      <MyNavbar />

      {/* =====================================================
          SIMPLE BANNER
      ===================================================== */}

      <SimpleBanner
        title="All Deals"
        subtitle="Discover amazing deals from verified sellers."
        breadcrumbs={[
          {
            label: "Home",
            href: "/",
          },
          {
            label: "All Deals",
            href: "/deals",
          },
          {
            label: "Deal Details",
            current: true,
          },
        ]}
      />

      {/* =====================================================
          DEAL CONTENT
      ===================================================== */}

      <Container className="py-4">
        {/* =================================================
            BACK BUTTON

            IMPORTANT:
            Navbar + Banner ke BAAD hai
        ================================================= */}

        <div className="deal-details-back-area">
          <button
            type="button"
            className="deal-details-back-btn"
            onClick={() => navigate("/deals")}
          >
            <FiArrowLeft className="deal-details-back-btn-icon" />

            <span>Back to Deals</span>
          </button>
        </div>

        {/* =================================================
            MAIN DEAL
        ================================================= */}

        <Row className="g-5 align-items-start">
          {/* =================================================
              IMAGE
          ================================================= */}

          <Col lg={6}>
            <div className="deal-details-image-wrapper">
              <img
                src={`${baseUrlForFiles}/${deal.deal_image}`}
                alt={deal.deal_title}
                className="deal-details-image"
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />

              {/* DEAL TYPE */}

              <span className="deal-details-type">
                {deal.deal_type === "Instant Deals" && "⚡ Instant"}

                {deal.deal_type === "Hot Deals" && "🔥 Hot"}

                {deal.deal_type === "Trending Deals" && "📈 Trending"}
              </span>

              {/* DISCOUNT */}

              {Number(deal.discount_percentage) > 0 && (
                <span className="deal-details-discount">
                  {deal.discount_percentage}% OFF
                </span>
              )}

              {/* SOLD */}

              {isSold && <div className="deal-status-overlay">SOLD OUT</div>}

              {/* STARTS SOON */}

              {isNotStarted && !isSold && (
                <div className="deal-coming-overlay">STARTS SOON</div>
              )}
            </div>
          </Col>

          {/* =================================================
              DETAILS
          ================================================= */}

          <Col lg={6}>
            <div className="deal-details-content">
              {/* CATEGORY */}

              <div className="deal-details-category">
                {deal.tpcategory?.categoryName || "Special Deal"}
              </div>

              {/* TITLE */}

              <h1 className="deal-main-title">{deal.deal_title}</h1>

              {/* =================================================
                  RATING
              ================================================= */}

              <div className="deal-details-rating">
                <FaStar />

                <span>{rating.toFixed(1)}</span>

                <small>
                  ({reviews} {reviews === 1 ? "review" : "reviews"})
                </small>
              </div>

              {/* =================================================
                  PRICE
              ================================================= */}

              <div className="deal-details-price">
                {deal.actual_price && (
                  <span className="old-price">
                    Rs. {Number(deal.actual_price).toLocaleString()}
                  </span>
                )}

                <span className="new-price">
                  Rs. {Number(deal.deal_price).toLocaleString()}
                </span>
              </div>

              {/* =================================================
                  LOCATION
              ================================================= */}

              {deal.deal_location && (
                <div className="deal-info-item">
                  <FaMapMarkerAlt />

                  <span>{deal.deal_location}</span>
                </div>
              )}

              {/* =================================================
                  START TIME
              ================================================= */}

              {deal.start_at && (
                <div className="deal-start-info">
                  <strong>
                    {isNotStarted ? "Deal Starts" : "Deal Started"}
                  </strong>

                  <span>{formatPakistanDateTime(deal.start_at)}</span>
                </div>
              )}

              {/* =================================================
                  EXPIRY
              ================================================= */}

              {deal.expires_at && (
                <div className="deal-expiry-info">
                  <strong>Deal Expires</strong>

                  <span>{formatPakistanDateTime(deal.expires_at)}</span>
                </div>
              )}

              {/* =================================================
                  DESCRIPTION
              ================================================= */}

              <div className="deal-description">
                <h4>About This Deal</h4>

                <p>
                  {deal.description ||
                    "Enjoy this amazing deal at a special price."}
                </p>
              </div>

              {/* =================================================
                  SERVICES
              ================================================= */}

              {services.length > 0 && (
                <div className="deal-services">
                  <h4>What's Included</h4>

                  {services.map((service, index) => (
                    <div className="deal-service" key={index}>
                      <FaCheck />

                      <span>
                        {service.itemname}

                        {service.price && (
                          <> — Rs. {Number(service.price).toLocaleString()}</>
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* =================================================
                  ADD TO CART

                  Guest:
                  Login to Add to Cart

                  Logged in:
                  Actual Add to Cart
              ================================================= */}

              {!loggedInCustomer ? (
                <Button
                  className="deal-add-cart-btn"
                  onClick={() => navigate("/login")}
                >
                  <FiShoppingCart />
                  Add to Cart
                </Button>
              ) : (
                <Button
                  className={`deal-add-cart-btn ${
                    isInCart ? "deal-added-btn" : ""
                  }`}
                  disabled={isSold || isExpired || isProcessing}
                  onClick={addToCart}
                >
                  <FiShoppingCart />

                  {isSold
                    ? "Sold Out"
                    : isExpired
                      ? "Deal Expired"
                      : isProcessing
                        ? "Adding..."
                        : isInCart
                          ? "Already in Cart"
                          : isNotStarted
                            ? "Add to Cart — Starts Soon"
                            : "Add to Cart"}
                </Button>
              )}

              {/* =================================================
                  NOT STARTED MESSAGE
              ================================================= */}

              {isNotStarted && !isSold && (
                <div className="deal-not-started-message">
                  <strong>⏰ This deal hasn't started yet</strong>

                  <p>
                    You can add this deal to your cart now. Purchase will be
                    available after the deal starts.
                  </p>
                </div>
              )}
            </div>
          </Col>
        </Row>

        {/* =====================================================
            FULL DESCRIPTION
        ===================================================== */}

        <Row className="mt-5">
          <Col lg={12}>
            <Card className="deal-full-description">
              <Card.Body>
                <h3>Deal Details</h3>

                <p>
                  {deal.description || "No additional description available."}
                </p>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* =====================================================
          FOOTER
      ===================================================== */}

      <Footer />
    </div>
  );
}
